// src/components/tools/education/LessonManagerTool.jsx
// Teacher-facing Academic Operations Tool: Plan → Teach → Record → Assess → Reflect
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye,
  Calendar, BookOpen, Target, Users, Clock, CheckCircle, GraduationCap,
  List, LayoutGrid, ChevronRight, ChevronLeft, Loader2, Save, X,
  FileText, BarChart3, TrendingUp, RefreshCw, ThumbsUp, ThumbsDown,
  AlertCircle
} from 'lucide-react';

// ============================================================
// CONSTANTS & ENUMS
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  ATP_LIST: 'ATP_LIST',
  ATP_FORM: 'ATP_FORM',
  ATP_DETAIL: 'ATP_DETAIL',
  LESSON_PLANS: 'LESSON_PLANS',
  CLASS_FORM: 'CLASS_FORM',         
  LEARNER_FORM: 'LEARNER_FORM',
  TIMETABLE: 'TIMETABLE',
  CLASSES_LEARNERS: 'CLASSES_LEARNERS',
  ASSESSMENTS: 'ASSESSMENTS',
  REPORTS: 'REPORTS'
};

const ATPStatus = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  DELAYED: 'delayed',
};

const LessonStatus = {
    PLANNED: 'planned',
    TAUGHT: 'taught',
    POSTPONED: 'postponed',
    MISSED: 'missed',
};

const ItemStatus = { // For sub-topics/lessons
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

const EngagementScore = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
};

const ItemType = {
  TOPIC: 'topic',
  SUB_TOPIC: 'sub_topic'
};

const ACADEMIC_YEARS = [...Array(5)].map((_, i) => new Date().getFullYear() - 1 + i);
const GRADES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const SUBJECTS = ['Maths', 'Science', 'English', 'History', 'Geography', 'Life Orientation', 'Technology'];
const TERMS = [1, 2, 3, 4];

// ⬅️ FIX 1: MONTHS is now defined
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ============================================================
// HELPER FUNCTIONS & UI COMPONENTS
// ============================================================

const formatDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStatusConfig = (status) => {
  const configs = {
    [ATPStatus.PLANNED]: { label: 'Planned', color: 'gray', icon: BookOpen, bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
    [ATPStatus.IN_PROGRESS]: { label: 'In Progress', color: 'blue', icon: Clock, bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
    [ATPStatus.COMPLETED]: { label: 'Completed', color: 'emerald', icon: CheckCircle, bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    [ATPStatus.DELAYED]: { label: 'Delayed', color: 'amber', icon: AlertCircle, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  };
  return configs[status] || configs[ATPStatus.PLANNED];
};

// ⬅️ FIX 2: StatusBadge is now defined
const StatusBadge = ({ status, size = 'sm' }) => {
  const config = getStatusConfig(status);
  const Icon = config.icon;
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${config.bgColor} ${config.textColor} ${sizeClasses}`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {config.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color = 'indigo', subValue, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const ProgressBar = ({ progress = 0, size = 'md', color = 'indigo' }) => {
  const heights = { sm: 'h-1.5', md: 'h-2' };
  const baseColor = progress >= 100 ? 'bg-emerald-500' : `bg-${color}-500`;
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 bg-gray-200 rounded-full overflow-hidden ${heights[size]}`}>
        <div
          className={`h-full rounded-full transition-all ${baseColor}`}
          style={{ width: `${Math.min(100, progress)}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 w-10 text-right">{Math.min(100, progress)}%</span>
    </div>
  );
};

// ============================================================
// DASHBOARD
// ============================================================

const Dashboard = ({ stats, onSelectView }) => {
  const currentYear = new Date().getFullYear();
  // ⬅️ FIX 3: Ensures MONTHS is used safely after being defined
  const currentMonth = MONTHS[new Date().getMonth()];

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Academic Dashboard</h1>
        <p className="text-gray-500">Overview for {currentMonth} {currentYear}</p>

        {/* Core Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Target}
            label="% ATP Coverage"
            value={`${stats.atpCoverage}%`}
            color="indigo"
            subValue={`${stats.atpTopicsCompleted}/${stats.atpTotalTopics} Topics`}
            onClick={() => onSelectView(ViewState.ATP_LIST)}
          />
          <StatCard
            icon={BookOpen}
            label="Lessons Planned"
            value={stats.lessonsPlanned}
            color="purple"
            subValue={`${stats.lessonsDelivered} Delivered`}
            onClick={() => onSelectView(ViewState.LESSON_PLANS)}
          />
          <StatCard
            icon={CheckCircle}
            label="Lessons This Week"
            value={stats.lessonsThisWeek}
            color="emerald"
            subValue={`${stats.lessonsCompletedThisWeek} Completed`}
            onClick={() => onSelectView(ViewState.TIMETABLE)}
          />
          <StatCard
            icon={BarChart3}
            label="Avg Class Engagement"
            value={`${stats.avgEngagement}`}
            color="blue"
            subValue={`${stats.classesTaught} Classes Taught`}
            onClick={() => onSelectView(ViewState.CLASSES_LEARNERS)}
          />
        </div>

        {/* Upcoming & Assessments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upcoming Lessons */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="font-semibold text-lg text-gray-900">Upcoming Lessons</h2>
              <button onClick={() => onSelectView(ViewState.TIMETABLE)} className="text-sm text-indigo-600 hover:underline">
                View Timetable
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {stats.upcomingLessons.length > 0 ? (
                stats.upcomingLessons.slice(0, 5).map((lesson, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{lesson.time} - {lesson.title}</p>
                      <p className="text-sm text-gray-500">{lesson.class}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="p-4 text-gray-500 text-sm">No lessons scheduled for the next 7 days.</p>
              )}
            </div>
          </div>

          {/* Assessments Due */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="font-semibold text-lg text-gray-900">Assessments Due</h2>
              <button onClick={() => onSelectView(ViewState.ASSESSMENTS)} className="text-sm text-indigo-600 hover:underline">
                View All
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {stats.assessmentsDue.length > 0 ? (
                stats.assessmentsDue.slice(0, 5).map((assessment, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                    <FileText className="w-5 h-5 text-amber-500" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{assessment.name}</p>
                      <p className="text-sm text-gray-500">{assessment.class} - Due: {formatDate(assessment.dueDate)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="p-4 text-gray-500 text-sm">No assessments due soon.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ATP LIST VIEW
// ============================================================

const ATPCard = ({ atp, onSelect, onEdit, onDelete }) => {
  const statusConfig = getStatusConfig(atp.status);
  const totalTopics = atp.topics?.length || 0;
  const completedTopics = atp.topics?.filter(t => t.status === ATPStatus.COMPLETED).length || 0;
  const progress = totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0;

  return (
    <div className={`bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all`}>
      <div className={`h-2 bg-${statusConfig.color}-500`} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{atp.name}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {atp.grade} / {atp.subject} / {atp.year}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(atp); }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete ATP?')) onDelete(atp.id); }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mb-4">
          <ProgressBar progress={progress} color={statusConfig.color} size="sm" />
          <p className="text-xs text-gray-500 mt-1">{completedTopics} of {totalTopics} Topics Covered</p>
        </div>
        
        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          <StatusBadge status={atp.status} />
          <button
            onClick={() => onSelect(atp)}
            className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl text-sm font-medium"
          >
            <ChevronRight className="w-4 h-4 inline-block -mt-0.5" /> View Plan
          </button>
        </div>
      </div>
    </div>
  );
};

const ATPList = ({ atps, onSelect, onEdit, onDelete, onCreate }) => (
  <div className="h-full overflow-y-auto">
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Annual Teaching Plans (ATP)</h1>
        <button
          onClick={onCreate}
          className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New ATP
        </button>
      </div>

      {atps.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-xl border border-gray-100">
          <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-900">No ATPs Found</h3>
          <p className="text-gray-500 text-sm">Create an ATP to structure your year's curriculum.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {atps.map(atp => (
            <ATPCard 
              key={atp.id} 
              atp={atp} 
              onSelect={onSelect} 
              onEdit={onEdit} 
              onDelete={onDelete} 
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

// ============================================================
// ATP FORM
// ============================================================

const ATPForm = ({ atp, onSave, onCancel, saving }) => {
  const isNew = !atp?.id;
  const [formData, setFormData] = useState({
    name: atp?.name || '',
    grade: atp?.grade || GRADES[8], // Default to Grade 9
    subject: atp?.subject || SUBJECTS[0],
    year: atp?.year || new Date().getFullYear(),
    status: atp?.status || ATPStatus.PLANNED,
    topics: atp?.topics || [], // Topics array managed separately
    ...atp
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-6xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <X className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNew ? 'Create New ATP' : 'Edit ATP Details'}
        </h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Grade 9 Maths 2024 Curriculum"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={formData.grade}
              onChange={(e) => setFormData(prev => ({ ...prev, grade: parseInt(e.target.value) }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {GRADES.map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <select
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={formData.year}
              onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {Object.values(ATPStatus).map(s => <option key={s} value={s}>{getStatusConfig(s).label}</option>)}
            </select>
          </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? 'Create Plan' : 'Update Details'}
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// ATP DETAIL VIEW
// ============================================================

const ATPEditor = ({ atp, onSaveTopics, saving }) => {
  const [topics, setTopics] = useState(atp.topics || []);
  const [newTopic, setNewTopic] = useState({ title: '', term: TERMS[0], duration: 1, type: ItemType.TOPIC, status: ATPStatus.PLANNED, subTopics: [] });
  const [newSubTopic, setNewSubTopic] = useState({});

  const handleAddTopic = () => {
    if (newTopic.title.trim()) {
      setTopics(prev => [...prev, { ...newTopic, id: Date.now().toString() }]);
      setNewTopic(prev => ({ ...prev, title: '' }));
    }
  };

  const handleUpdateTopic = (id, field, value) => {
    setTopics(prev => prev.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const handleRemoveTopic = (id) => {
    setTopics(prev => prev.filter(t => t.id !== id));
  };
  
  const handleAddSubTopic = (topicId) => {
      if (newSubTopic.title && newSubTopic.topicId === topicId) {
          const newST = { ...newSubTopic, id: Date.now().toString(), type: ItemType.SUB_TOPIC, status: ItemStatus.PLANNED };
          setTopics(prev => prev.map(t => 
              t.id === topicId ? { ...t, subTopics: [...(t.subTopics || []), newST] } : t
          ));
          setNewSubTopic({});
      }
  };
  
  const handleUpdateSubTopic = (topicId, subTopicId, field, value) => {
      setTopics(prev => prev.map(t => 
          t.id === topicId ? { 
              ...t, 
              subTopics: t.subTopics.map(st => 
                  st.id === subTopicId ? { ...st, [field]: value } : st
              ) 
          } : t
      ));
  };

  const handleRemoveSubTopic = (topicId, subTopicId) => {
      setTopics(prev => prev.map(t => 
          t.id === topicId ? { ...t, subTopics: t.subTopics.filter(st => st.id !== subTopicId) } : t
      ));
  };
  
  const handleSave = () => {
      onSaveTopics(atp.id, topics);
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
        {/* Topic Input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newTopic.title}
            onChange={(e) => setNewTopic(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Topic Title (e.g., Algebraic Expressions)"
            className="flex-grow px-4 py-2.5 border border-gray-200 rounded-xl"
          />
          <select
              value={newTopic.term}
              onChange={(e) => setNewTopic(prev => ({ ...prev, term: parseInt(e.target.value) }))}
              className="px-4 py-2.5 border border-gray-200 rounded-xl"
          >
              {TERMS.map(t => <option key={t} value={t}>Term {t}</option>)}
          </select>
          <input
              type="number"
              min="1"
              value={newTopic.duration}
              onChange={(e) => setNewTopic(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
              placeholder="Weeks"
              className="w-20 px-4 py-2.5 border border-gray-200 rounded-xl text-center"
          />
          <button
            onClick={handleAddTopic}
            type="button"
            className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="w-4 h-4" /> Add Topic
          </button>
        </div>
        
        {/* Topic List */}
        <div className="space-y-4">
            {topics.length === 0 ? (
                <div className="py-12 text-center bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-gray-500">No topics added to this plan yet.</p>
                </div>
            ) : (
                topics.map(topic => (
                    <div key={topic.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        {/* Topic Header */}
                        <div className={`flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200`}>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <span className="font-bold text-gray-900 truncate flex-1 min-w-0">{topic.title}</span>
                                <span className="text-sm text-gray-500 flex-shrink-0">Term {topic.term} ({topic.duration} Weeks)</span>
                                <StatusBadge status={topic.status} size="sm" />
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                    onClick={() => handleUpdateTopic(topic.id, 'status', topic.status === ATPStatus.COMPLETED ? ATPStatus.IN_PROGRESS : ATPStatus.COMPLETED)}
                                    title="Toggle Status"
                                    className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleRemoveTopic(topic.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        
                        {/* Sub-Topics */}
                        <div className="p-4 space-y-3">
                            {(topic.subTopics || []).map(subTopic => (
                                <div key={subTopic.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                    <input
                                        type="text"
                                        value={subTopic.title}
                                        onChange={(e) => handleUpdateSubTopic(topic.id, subTopic.id, 'title', e.target.value)}
                                        placeholder="Sub-Topic Title"
                                        className="flex-grow px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                                    />
                                    <input
                                        type="number"
                                        min="1"
                                        value={subTopic.duration}
                                        onChange={(e) => handleUpdateSubTopic(topic.id, subTopic.id, 'duration', parseInt(e.target.value) || 1)}
                                        placeholder="Lessons"
                                        className="w-16 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-center"
                                    />
                                    <StatusBadge status={subTopic.status} size="sm" />
                                    <button
                                        onClick={() => handleRemoveSubTopic(topic.id, subTopic.id)}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                            
                            {/* Add Sub-Topic Input */}
                            <div className="flex gap-2 pt-2 border-t border-gray-100">
                                <input
                                    type="text"
                                    value={newSubTopic.topicId === topic.id ? newSubTopic.title : ''}
                                    onChange={(e) => setNewSubTopic({ topicId: topic.id, title: e.target.value, duration: 1 })}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubTopic(topic.id))}
                                    placeholder="Add sub-topic..."
                                    className="flex-grow px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                                />
                                <button
                                    onClick={() => handleAddSubTopic(topic.id)}
                                    type="button"
                                    className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm hover:bg-indigo-100 flex items-center"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
        
        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
                onClick={handleSave}
                type="button"
                disabled={saving}
                className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save ATP Content
            </button>
        </div>
    </div>
  );
};


const ATPDetail = ({ atp, items, onBack, onEditATP, onSaveTopics }) => {
  const [activeTab, setActiveTab] = useState('editor'); // 'editor', 'pacing', 'reports'

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{atp.name}</h1>
              <p className="text-gray-500">{atp.grade} / {atp.subject} / {atp.year}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEditATP(atp)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" /> Edit Details
            </button>
            <button
              onClick={() => {}} // Placeholder for Link to Lesson Plan
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" /> Link Lesson
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-gray-200">
          {['editor', 'pacing', 'reports'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'editor' ? 'Curriculum Editor' : tab === 'pacing' ? 'Pacing & Progress' : 'ATP Reports'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          {activeTab === 'editor' && (
            <ATPEditor atp={atp} onSaveTopics={onSaveTopics} saving={false} />
          )}
          {activeTab === 'pacing' && (
            <div className="py-12 text-center text-gray-500">
              <RefreshCw className="w-10 h-10 mx-auto mb-3" />
              Pacing and live progress tracking coming soon...
            </div>
          )}
          {activeTab === 'reports' && (
            <div className="py-12 text-center text-gray-500">
              <BarChart3 className="w-10 h-10 mx-auto mb-3" />
              Detailed ATP coverage reports coming soon...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClassForm = ({ classData, onSave, onCancel, saving }) => {
  const isNew = !classData?.id;
  const [formData, setFormData] = useState({
    name: '', grade: GRADES[8], subject: SUBJECTS[0], description: '', learnerCount: 0, ...classData
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5 text-gray-600" /></button>
        <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'Create New Class' : 'Edit Class Details'}</h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Class Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Grade 9 Maths A"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={formData.grade}
              onChange={(e) => setFormData(prev => ({ ...prev, grade: parseInt(e.target.value) }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {GRADES.map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <select
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (e.g. Set 1, remedial)</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? 'Create Class' : 'Update Class'}
          </button>
        </div>
      </div>
    </form>
  );
};


// ============================================================
// NEW MODULE: LEARNER FORM
// ============================================================
const LearnerForm = ({ learner, classId, onSave, onCancel, saving }) => {
  const isNew = !learner?.id;
  const [formData, setFormData] = useState({
    name: '', schoolId: '', email: '', notes: '', classId, ...learner
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5 text-gray-600" /></button>
        <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'Add New Learner' : 'Edit Learner Details'}</h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Sarah Jones"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School ID (Optional)</label>
            <input
              type="text"
              value={formData.schoolId}
              onChange={(e) => setFormData(prev => ({ ...prev, schoolId: e.target.value }))}
              placeholder="12345"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (Parent/Guardian)</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="parent@example.com"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Interventions</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? 'Add Learner' : 'Update Learner'}
          </button>
        </div>
      </div>
    </form>
  );
};


// ============================================================
// NEW MODULE: CLASSES & LEARNERS LIST
// ============================================================
const ClassesLearnersList = ({ classes, learners, onSelectView, onEditClass, onEditLearner, onDeleteClass, onDeleteLearner, onCreateClass, onCreateLearner }) => {
  const [activeClass, setActiveClass] = useState(null);
  const classLearners = useMemo(() => {
    if (!activeClass) return [];
    // Calculate basic learner stats (mocked fields for now)
    return learners.filter(l => l.classId === activeClass.id).map(l => ({
        ...l,
        engagementScore: l.engagementScore || EngagementScore.MEDIUM,
        absences: l.absences || Math.floor(Math.random() * 5),
    }));
  }, [activeClass, learners]);
  
  const classStats = useMemo(() => {
    if (!activeClass) return {};
    const totalLearners = classLearners.length;
    const highEngage = classLearners.filter(l => l.engagementScore === EngagementScore.HIGH).length;
    const avgAbsences = totalLearners ? (classLearners.reduce((sum, l) => sum + l.absences, 0) / totalLearners).toFixed(1) : 0;
    
    return { totalLearners, highEngage, avgAbsences };
  }, [activeClass, classLearners]);


  const AllClassesList = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {classes.map(c => {
        const count = learners.filter(l => l.classId === c.id).length;
        return (
          <div key={c.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{c.name}</h3>
                <p className="text-sm text-gray-500">{c.grade} / {c.subject}</p>
                <p className="text-xs text-gray-400 mt-1">{c.description}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onEditClass(c); }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete class?')) onDeleteClass(c.id); }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
              <span className="font-medium text-gray-700">{count} Learners</span>
              <button
                onClick={() => setActiveClass(c)}
                className="px-3 py-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium"
              >
                View List <ChevronRight className="w-4 h-4 inline-block -mt-0.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const LearnerDetailView = () => (
    <div className="space-y-6">
      {/* Class Header */}
      <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100">
        <div className="flex items-center gap-3">
            <button onClick={() => setActiveClass(null)} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
            <div>
                <h2 className="text-xl font-bold text-gray-900">{activeClass.name} List</h2>
                <p className="text-sm text-gray-500">{activeClass.grade} / {activeClass.subject}</p>
            </div>
        </div>
        <button
          onClick={() => onCreateLearner(activeClass.id)}
          className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Learner
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Total Learners" value={classStats.totalLearners} color="indigo" />
        <StatCard icon={ThumbsUp} label="High Engagement" value={`${classStats.highEngage} (${(classStats.highEngage / classStats.totalLearners * 100).toFixed(0)}%)`} color="emerald" />
        <StatCard icon={Clock} label="Avg Absences (Term)" value={classStats.avgAbsences} color="amber" />
      </div>

      {/* Learner Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Learner Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">School ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Absences</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {classLearners.length === 0 ? (
                <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500 text-sm">No learners added to this class.</td>
                </tr>
              ) : (
                classLearners.map(learner => (
                  <tr key={learner.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{learner.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{learner.schoolId || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            learner.engagementScore === EngagementScore.HIGH ? 'bg-emerald-100 text-emerald-800' :
                            learner.engagementScore === EngagementScore.MEDIUM ? 'bg-blue-100 text-blue-800' :
                            'bg-amber-100 text-amber-800'
                        }`}>
                            {learner.engagementScore}
                        </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{learner.absences}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onEditLearner(learner)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { if(window.confirm('Delete learner?')) onDeleteLearner(learner.id); }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Classes & Learners</h1>
          <button
            onClick={onCreateClass}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Class
          </button>
        </div>
        
        {activeClass ? (
            <LearnerDetailView />
        ) : classes.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-xl border border-gray-100">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900">No Classes Set Up</h3>
            <p className="text-gray-500 text-sm">Start by creating your subject classes.</p>
          </div>
        ) : (
          <AllClassesList />
        )}
      </div>
    </div>
  );
};

const LessonPlanForm = ({ lesson, atps, onSave, onCancel, saving }) => {
    const isNew = !lesson?.id;
    const [formData, setFormData] = useState({
        title: '',
        linkedATPId: '', // ID of the parent ATP
        linkedTopicId: '', // ID of the specific Topic/Sub-Topic
        grade: GRADES[8],
        subject: SUBJECTS[0],
        objectives: '',
        activities: '',
        resources: '',
        assessmentStrategy: '',
        status: LessonStatus.PLANNED,
        ...lesson
    });

    const filteredAtps = atps.filter(a => a.grade === formData.grade && a.subject === formData.subject);
    const selectedATP = filteredAtps.find(a => a.id === formData.linkedATPId);
    
    // Flatten topics from the selected ATP for linking
    const availableTopics = useMemo(() => {
        if (!selectedATP?.topics) return [];
        let list = [];
        selectedATP.topics.forEach(t => {
            list.push({ id: t.id, title: t.title, type: 'Topic', term: t.term });
            (t.subTopics || []).forEach(st => {
                list.push({ id: st.id, title: `${t.title} / ${st.title}`, type: 'Sub-Topic', term: t.term });
            });
        });
        return list;
    }, [selectedATP]);
    
    // Auto-select first ATP if none is linked
    useEffect(() => {
        if (isNew && filteredAtps.length > 0 && !formData.linkedATPId) {
            setFormData(prev => ({ ...prev, linkedATPId: filteredAtps[0].id }));
        }
    }, [isNew, filteredAtps, formData.linkedATPId]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.linkedATPId) return;
        onSave(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'Create New Lesson Plan' : 'Edit Lesson Plan'}</h1>
                <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5 text-gray-600" /></button>               
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
                {/* Core Details */}
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-indigo-600" /> Lesson Core
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Lesson Title *</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g., Introduction to Quadratic Equations"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                        <select
                            value={formData.grade}
                            onChange={(e) => setFormData(prev => ({ ...prev, grade: parseInt(e.target.value), linkedATPId: '' }))}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                        >
                            {GRADES.map(g => <option key={g} value={g}>Grade {g}</option>)}
                        </select>
                    </div>
                </div>
                
                {/* ATP Linking */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Subject ATP *</label>
                        <select
                            value={formData.linkedATPId}
                            onChange={(e) => setFormData(prev => ({ ...prev, linkedATPId: e.target.value, linkedTopicId: '' }))}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                            required
                        >
                            <option value="">Select ATP...</option>
                            {filteredAtps.map(a => (
                                <option key={a.id} value={a.id}>{a.name} (Term {a.topics?.[0]?.term || 'All'})</option>
                            ))}
                        </select>
                        {filteredAtps.length === 0 && <p className="text-xs text-red-500 mt-1">No ATPs found for Grade {formData.grade}.</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Topic/Sub-Topic</label>
                        <select
                            value={formData.linkedTopicId}
                            onChange={(e) => setFormData(prev => ({ ...prev, linkedTopicId: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                            disabled={!formData.linkedATPId || availableTopics.length === 0}
                        >
                            <option value="">Select Topic/Sub-Topic...</option>
                            {availableTopics.map(t => (
                                <option key={t.id} value={t.id}>{t.title} ({t.type})</option>
                            ))}
                        </select>
                        {!formData.linkedATPId && <p className="text-xs text-gray-500 mt-1">Select an ATP first.</p>}
                        {formData.linkedATPId && availableTopics.length === 0 && <p className="text-xs text-red-500 mt-1">ATP has no defined topics.</p>}
                    </div>
                </div>

                {/* Content */}
                <h2 className="font-semibold text-gray-900 flex items-center gap-2 pt-4 border-t border-gray-100">
                    <Target className="w-5 h-5 text-purple-600" /> Objectives & Activities
                </h2>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Learning Objectives (CAPS/Specific Aims)</label>
                    <textarea
                        value={formData.objectives}
                        onChange={(e) => setFormData(prev => ({ ...prev, objectives: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                        placeholder="Learners will be able to identify coefficients and constants in an expression..."
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teaching Activities / Lesson Flow</label>
                    <textarea
                        value={formData.activities}
                        onChange={(e) => setFormData(prev => ({ ...prev, activities: e.target.value }))}
                        rows={5}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                        placeholder="Introduction: 10 mins (Review prior knowledge). Main Activity: 30 mins (Group work on problem set)..."
                    />
                </div>
                
                {/* Resources & Assessment */}
                <h2 className="font-semibold text-gray-900 flex items-center gap-2 pt-4 border-t border-gray-100">
                    <FileText className="w-5 h-5 text-emerald-600" /> Resources & Assessment
                </h2>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resources</label>
                    <textarea
                        value={formData.resources}
                        onChange={(e) => setFormData(prev => ({ ...prev, resources: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                        placeholder="Textbook pages 45-50, Worksheet 3.1, Video link..."
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Strategy</label>
                    <textarea
                        value={formData.assessmentStrategy}
                        onChange={(e) => setFormData(prev => ({ ...prev, assessmentStrategy: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                        placeholder="Informal: Observation of group work. Formal: Exit ticket (3 questions) to assess objective 1..."
                    />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">Cancel</button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isNew ? 'Save Lesson Plan' : 'Update Plan'}
                    </button>
                </div>
            </div>
        </form>
    );
};


// ============================================================
// NEW MODULE: LESSON PLANS LIST
// ============================================================
const LessonPlansList = ({ lessons, atps, onSelectView, onEditLesson, onDeleteLesson, onCreateLesson }) => {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredLessons = useMemo(() => {
        if (!searchQuery) return lessons;
        const q = searchQuery.toLowerCase();
        return lessons.filter(l => 
            l.title?.toLowerCase().includes(q) || 
            l.objectives?.toLowerCase().includes(q)
        );
    }, [lessons, searchQuery]);

    const getLinkedInfo = (lesson) => {
        const atp = atps.find(a => a.id === lesson.linkedATPId);
        if (!atp) return 'Unlinked';
        
        let topicTitle = atp.name;
        if (lesson.linkedTopicId) {
            const topic = atp.topics.find(t => t.id === lesson.linkedTopicId || t.subTopics?.some(st => st.id === lesson.linkedTopicId));
            if (topic) {
                const subTopic = topic.subTopics?.find(st => st.id === lesson.linkedTopicId);
                topicTitle = subTopic ? `${topic.title} / ${subTopic.title}` : topic.title;
            }
        }
        return topicTitle;
    };


    return (
        <div className="h-full overflow-y-auto">
            <div className="max-w-6xl mx-auto p-6 space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">Lesson Plans ({lessons.length})</h1>
                    <button
                        onClick={onCreateLesson}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" /> New Lesson
                    </button>
                </div>

                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search lesson titles or objectives..."
                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl"
                    />
                </div>
                
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title / Objectives</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked Topic</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredLessons.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-12 text-center text-gray-500 text-sm">No lesson plans found.</td>
                                    </tr>
                                ) : (
                                    filteredLessons.map(lesson => (
                                        <tr key={lesson.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <p className="font-medium text-gray-900">{lesson.title}</p>
                                                <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{lesson.objectives}</p>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{getLinkedInfo(lesson)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={lesson.status || LessonStatus.PLANNED} size="sm" />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button onClick={() => onEditLesson(lesson)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Edit Plan">
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => { if(window.confirm('Delete lesson?')) onDeleteLesson(lesson.id); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete Plan">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// LESSON MANAGER TOOL - MAIN COMPONENT
// ============================================================

const LessonManagerTool = ({ bookId, customStyling = {}, toolsService, onBack, workspaceId = 'default' }) => {
  // Global State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(ViewState.DASHBOARD);
  
  // Data State
  const [atps, setAtps] = useState([]);
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]); // Lesson Plans
  const [learners, setLearners] = useState([]); // ⬅️ DEFINED HERE
  
  // Active/Editing State
  const [activeATP, setActiveATP] = useState(null);
  const [editingATP, setEditingATP] = useState(null);
  const [activeLearner, setActiveLearner] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);


  // --- Data Loading ---
  useEffect(() => {
    loadData();
  }, [bookId, workspaceId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [atpsData, classesData, lessonsData, learnersData] = await Promise.all([ // ⬅️ LOAD LEARNER DATA
        toolsService.getToolData(bookId, 'lesson-atps', workspaceId),
        toolsService.getToolData(bookId, 'classes', workspaceId),
        toolsService.getToolData(bookId, 'lesson-plans', workspaceId),
        toolsService.getToolData(bookId, 'learners', workspaceId) // ⬅️ FETCH LEARNER DATA
      ]);
      setAtps(atpsData || []);
      setClasses(classesData || []);
      setLessons(lessonsData || []);
      setLearners(learnersData || []); // ⬅️ SET LEARNER STATE
    } catch (error) {
      console.error('Error loading Lesson Manager data:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- Utility Functions ---
  const calculateDashboardStats = useMemo(() => {
    const totalTopics = atps.reduce((sum, a) => sum + (a.topics?.length || 0), 0);
    const topicsCompleted = atps.reduce((sum, a) => sum + (a.topics?.filter(t => t.status === ATPStatus.COMPLETED).length || 0), 0);
    const atpCoverage = totalTopics > 0 ? Math.round((topicsCompleted / totalTopics) * 100) : 0;
    
    // Mock Data for other sections
    const lessonsPlanned = lessons.length;
    const lessonsDelivered = lessonsPlanned > 0 ? lessonsPlanned - Math.floor(Math.random() * 5) : 0;
    const lessonsThisWeek = 15;
    const lessonsCompletedThisWeek = 10;
    const avgEngagement = ['Medium', 'High'][Math.floor(Math.random() * 2)];
    const classesTaught = classes.length;
    
    const upcomingLessons = [
      { time: '08:00', title: 'Calculus Introduction', class: 'Grade 12 Maths A' },
      { time: '10:00', title: 'World War II Causes', class: 'Grade 9 History' },
      { time: '13:00', title: 'Cell Structure & Function', class: 'Grade 10 Science' },
    ];
    
    const assessmentsDue = [
      { name: 'Algebra Test', class: 'Grade 9 Maths', dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) },
      { name: 'Research Project', class: 'Grade 11 English', dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
    ];

    return {
      atpCoverage,
      atpTotalTopics: totalTopics,
      atpTopicsCompleted: topicsCompleted,
      lessonsPlanned,
      lessonsDelivered,
      lessonsThisWeek,
      lessonsCompletedThisWeek,
      avgEngagement,
      classesTaught,
      upcomingLessons,
      assessmentsDue
    };
  }, [atps, lessons, classes]);

  // --- CRUD Handlers ---

  // ATP Save (Details/Form)
  const handleSaveATPDetails = async (data) => {
    setSaving(true);
    try {
      const saved = await toolsService.saveToolData(bookId, 'lesson-atps', data, workspaceId);
      if (activeATP?.id === data.id) setActiveATP(saved);
      await loadData();
      setView(ViewState.ATP_LIST);
    } catch (error) {
      console.error('Error saving ATP:', error);
      alert('Failed to save ATP');
    } finally {
      setSaving(false);
    }
  };
  
  // ATP Save (Topics/Content)
  const handleSaveATPTopics = async (atpId, topics) => {
      setSaving(true);
      try {
          const atp = atps.find(a => a.id === atpId);
          await toolsService.saveToolData(bookId, 'lesson-atps', { ...atp, topics }, workspaceId);
          await loadData();
          alert('ATP content saved successfully.');
      } catch (error) {
          console.error('Error saving ATP Topics:', error);
          alert('Failed to save ATP Topics');
      } finally {
          setSaving(false);
      }
  };

  // ATP Delete
  const handleDeleteATP = async (atpId) => {
    try {
      await toolsService.deleteToolData(bookId, 'lesson-atps', atpId, workspaceId);
      await loadData();
    } catch (error) {
      console.error('Error deleting ATP:', error);
    }
  };

  // --- View Helpers ---

  const handleSelectATP = (atp) => {
    setActiveATP(atp);
    setView(ViewState.ATP_DETAIL);
  };
  
  const handleEditATPDetails = (atp) => {
      setEditingATP(atp);
      setView(ViewState.ATP_FORM);
  };

  const handleSaveClass = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'classes', data, workspaceId);
      await loadData();
      setView(ViewState.CLASSES_LEARNERS);
    } catch (error) {
      console.error('Error saving class:', error);
      alert('Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    try {
      // Delete all associated learners first
      const learnersToDelete = learners.filter(l => l.classId === classId);
      for (const learner of learnersToDelete) {
        await toolsService.deleteToolData(bookId, 'learners', learner.id, workspaceId);
      }
      await toolsService.deleteToolData(bookId, 'classes', classId, workspaceId);
      await loadData();
    } catch (error) {
      console.error('Error deleting class:', error);
    }
  };
  
  const handleSaveLearner = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'learners', data, workspaceId);
      await loadData();
      setActiveATP(null); // Clear editing states
      setEditingATP(null);
      setView(ViewState.CLASSES_LEARNERS); // Return to list view
    } catch (error) {
      console.error('Error saving learner:', error);
      alert('Failed to save learner');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLearner = async (learnerId) => {
    try {
      await toolsService.deleteToolData(bookId, 'learners', learnerId, workspaceId);
      await loadData();
    } catch (error) {
      console.error('Error deleting learner:', error);
    }
  };

  const handleSaveLesson = async (data) => {
        setSaving(true);
        try {
            await toolsService.saveToolData(bookId, 'lesson-plans', data, workspaceId);
            await loadData();
            setView(ViewState.LESSON_PLANS);
        } catch (error) {
            console.error('Error saving lesson plan:', error);
            alert('Failed to save lesson plan');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteLesson = async (lessonId) => {
        try {
            await toolsService.deleteToolData(bookId, 'lesson-plans', lessonId, workspaceId);
            await loadData();
        } catch (error) {
            console.error('Error deleting lesson plan:', error);
        }
    };
    
// ... (CRUD Handlers for ATPs, Classes, Learners remain the same) ...

  // --- View Helpers ---
    const handleEditLesson = (lesson) => {
        setActiveLesson(lesson);
        setView(ViewState.LESSON_PLAN_FORM);
    };

    const handleCreateLesson = () => {
        setActiveLesson(null);
        setView(ViewState.LESSON_PLAN_FORM);
    };
  
  // --- Render ---

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }
  
  const handleBackToDashboard = () => setView(ViewState.DASHBOARD);
  
  return (
    <div className="h-full w-full flex flex-col bg-gray-50">
      {/* Universal Back Button for non-Dashboard Views */}
      {view !== ViewState.DASHBOARD && view !== ViewState.ATP_DETAIL && view !== ViewState.CLASSES_LEARNERS && view !== ViewState.CLASS_FORM && view !== ViewState.LEARNER_FORM && view !== ViewState.LESSON_PLAN_FORM && (
        <div className="px-6 py-4 bg-white border-b border-gray-100">
            <button 
                onClick={
                    view === ViewState.LESSON_PLANS || view === ViewState.ATP_LIST ? handleBackToDashboard : 
                    view === ViewState.ATP_FORM ? () => setView(ViewState.ATP_LIST) : handleBackToDashboard
                } 
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
                <ArrowLeft className="w-5 h-5" /> Back
            </button>
        </div>
      )}

      {/* Dashboard */}
      {view === ViewState.DASHBOARD && (
        <Dashboard
          stats={calculateDashboardStats}
          onSelectView={(v) => {
            if (v === ViewState.ATP_LIST) setView(ViewState.ATP_LIST);
            else if (v === ViewState.CLASSES_LEARNERS) setView(ViewState.CLASSES_LEARNERS);
            else if (v === ViewState.LESSON_PLANS) setView(ViewState.LESSON_PLANS); // ⬅️ Route to new module
            else onBack(); // Default to app-level back for un-implemented views
          }}
        />
      )}

      {view === ViewState.ATP_LIST && (
        <ATPList
          atps={atps}
          onSelect={handleSelectATP}
          onEdit={handleEditATPDetails}
          onDelete={handleDeleteATP}
          onCreate={() => { setEditingATP(null); setView(ViewState.ATP_FORM); }}
        />
      )}
      
      {view === ViewState.ATP_FORM && (
        <ATPForm
          atp={editingATP}
          onSave={handleSaveATPDetails}
          onCancel={() => setView(ViewState.ATP_LIST)}
          saving={saving}
        />
      )}
      
      {view === ViewState.ATP_DETAIL && activeATP && (
        <ATPDetail
          atp={activeATP}
          items={[]} // Placeholder for lesson plan items
          onBack={() => { setActiveATP(null); setView(ViewState.ATP_LIST); }}
          onEditATP={handleEditATPDetails}
          onSaveTopics={handleSaveATPTopics}
        />
      )}

      {/* Classes & Learners List (New Main Module) */}
      {view === ViewState.CLASSES_LEARNERS && (
          <ClassesLearnersList
              classes={classes}
              learners={learners}
              onSelectView={setView}
              onCreateClass={() => { setEditingATP(null); setView(ViewState.CLASS_FORM); }}
              onEditClass={(c) => { setEditingATP(c); setView(ViewState.CLASS_FORM); }}
              onDeleteClass={handleDeleteClass}
              onCreateLearner={(classId) => { setActiveLearner({ classId }); setView(ViewState.LEARNER_FORM); }}
              onEditLearner={(l) => { setActiveLearner(l); setView(ViewState.LEARNER_FORM); }}
              onDeleteLearner={handleDeleteLearner}
          />
      )}
      
      {/* Class Form */}
      {view === ViewState.CLASS_FORM && (
          <ClassForm
              classData={editingATP}
              onSave={handleSaveClass}
              onCancel={() => setView(ViewState.CLASSES_LEARNERS)}
              saving={saving}
          />
      )}
      
      {/* Learner Form */}
      {view === ViewState.LEARNER_FORM && (
          <LearnerForm
              learner={activeLearner}
              classId={activeLearner?.classId}
              onSave={handleSaveLearner}
              onCancel={() => setView(ViewState.CLASSES_LEARNERS)}
              saving={saving}
          />
      )}

      {/* Lesson Plans List (New Main Module) */}
      {view === ViewState.LESSON_PLANS && (
          <LessonPlansList
              lessons={lessons}
              atps={atps}
              onSelectView={setView}
              onCreateLesson={handleCreateLesson}
              onEditLesson={handleEditLesson}
              onDeleteLesson={handleDeleteLesson}
          />
      )}

      {/* Lesson Plan Form */}
      {view === ViewState.LESSON_PLAN_FORM && (
          <LessonPlanForm
              lesson={activeLesson}
              atps={atps}
              onSave={handleSaveLesson}
              onCancel={() => setView(ViewState.LESSON_PLANS)}
              saving={saving}
          />
      )}

      {/* Placeholder Views */}
      {( view === ViewState.TIMETABLE || view === ViewState.CLASSES_LEARNERS || view === ViewState.ASSESSMENTS || view === ViewState.REPORTS) && (
        <div className="flex flex-col items-center justify-center flex-1 p-6">
          <GraduationCap className="w-16 h-16 text-indigo-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">
            {view.replace('_', ' ')}
          </h2>
          <p className="text-gray-500 mt-2">Module coming soon. Returning to main tools list.</p>
          <button
            onClick={onBack}
            className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
          >
            <ArrowLeft className="w-4 h-4 inline-block -mt-0.5" /> Back to Tools
          </button>
        </div>
      )}
      
      {/* Footer */}
      <div className="flex-shrink-0 p-3 text-center text-xs text-gray-400 border-t border-gray-100">
          Lesson Manager v1.0 - Academic Operations Tool
      </div>
    </div>
  );
};

export default LessonManagerTool;